import { useEffect } from 'react';
import { Link } from 'react-router-dom';

function LegalTable({ headers, rows }: { headers: string[]; rows: string[][] }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 my-4">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            {headers.map((h) => (
              <th
                key={h}
                className="px-4 py-3 text-left font-semibold text-gray-700 border-b border-gray-200 whitespace-nowrap"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => (
                <td key={j} className="px-4 py-3 text-gray-700 align-top">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Section({ number, title, children }: { number: string; title: string; children: React.ReactNode }) {
  return (
    <section className="mt-10">
      <h2 className="text-base font-bold text-gray-900 mb-3">
        {number} ({title})
      </h2>
      {children}
    </section>
  );
}

export default function TermsPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-10 bg-white border-b border-gray-100 px-4 sm:px-6 h-14 flex items-center">
        <Link
          to="/"
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-primary-600 transition-colors font-medium"
        >
          ← MaioS 홈으로
        </Link>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <h1 className="text-2xl font-bold text-gray-900">MaioS 이용약관</h1>
        <p className="mt-1 text-sm text-gray-500">시행일: 2026년 7월 3일</p>

        {/* 제1조 */}
        <Section number="제1조" title="목적">
          <p className="text-sm text-gray-700 leading-relaxed">
            이 약관은 김대웅(이하 "운영자")이 제공하는 MaioS 서비스(이하 "서비스")의 이용 조건 및
            절차, 운영자와 이용자 간의 권리·의무·책임 사항을 규정함을 목적으로 합니다.
          </p>
        </Section>

        {/* 제2조 */}
        <Section number="제2조" title="정의">
          <LegalTable
            headers={['용어', '정의']}
            rows={[
              ['서비스', 'maios.kr 도메인을 통해 제공하는 B2B 프로젝트 관리 플랫폼'],
              ['회원', '이 약관에 동의하고 회원가입을 완료한 자'],
              ['조직(Organization)', '서비스 내에서 팀 단위로 협업하는 그룹'],
              ['베타 서비스', '정식 출시 전 제공되는 무료 시범 서비스'],
            ]}
          />
        </Section>

        {/* 제3조 */}
        <Section number="제3조" title="약관의 효력 및 변경">
          <div className="text-sm text-gray-700 leading-relaxed space-y-2">
            <p>① 이 약관은 서비스 이용 신청 시 동의함으로써 효력이 발생합니다.</p>
            <p>
              ② 운영자는 필요한 경우 약관을 변경할 수 있으며, 변경 시 시행 7일 전에 서비스 내 공지 또는
              이메일로 안내합니다.
            </p>
            <p>
              ③ 변경된 약관에 동의하지 않는 회원은 서비스 이용을 중단하고 탈퇴할 수 있습니다. 변경 후에도
              서비스를 계속 이용하면 변경된 약관에 동의한 것으로 간주합니다.
            </p>
          </div>
        </Section>

        {/* 제4조 */}
        <Section number="제4조" title="이용계약의 체결">
          <div className="text-sm text-gray-700 leading-relaxed space-y-2">
            <p>
              ① 이용계약은 이용자가 약관에 동의하고 회원가입을 신청하면 운영자가 이를 승낙함으로써
              체결됩니다.
            </p>
            <p>② 운영자는 다음에 해당하는 경우 가입을 거부하거나 사후에 이용계약을 해지할 수 있습니다.</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>타인의 명의 또는 허위 정보를 사용한 경우</li>
              <li>이전에 서비스 이용 제한을 받은 이력이 있는 경우</li>
              <li>서비스 운영을 방해하거나 방해할 우려가 있는 경우</li>
            </ul>
          </div>
        </Section>

        {/* 제5조 */}
        <Section number="제5조" title="베타 서비스">
          <div className="text-sm text-gray-700 leading-relaxed space-y-2">
            <p>
              ① 현재 서비스는 <strong>베타 버전(2026년 7월 3일 ~ 2026년 9월 30일)</strong>으로
              제공됩니다.
            </p>
            <p>② 베타 기간 중에는 모든 기능을 무료로 이용할 수 있습니다.</p>
            <p>
              ③ 베타 기간 중 서비스 기능이 추가·변경되거나, 예기치 않은 오류로 일부 데이터가 손실될 수
              있습니다. 운영자는 베타 기간 중 발생한 데이터 손실에 대한 책임을 지지 않습니다.
            </p>
            <p>
              ④ 베타 기간 종료(2026년 9월 30일) 후에는 Free 플랜으로 자동 전환됩니다. 베타 참여 회원에게는
              유료 플랜 전환 시 별도 혜택이 제공될 예정이며, 세부 내용은 사전에 이메일로 안내합니다.
            </p>
          </div>
        </Section>

        {/* 제6조 */}
        <Section number="제6조" title="서비스의 제공 및 변경">
          <div className="text-sm text-gray-700 leading-relaxed space-y-2">
            <p>① 운영자는 연중무휴 24시간 서비스를 제공함을 원칙으로 합니다.</p>
            <p>
              ② 서버 점검, 장애, 기술적 문제 등으로 서비스가 일시 중단될 수 있으며, 계획된 중단의 경우
              사전에 공지합니다. 긴급 상황 시 사후 공지할 수 있습니다.
            </p>
            <p>
              ③ 운영자는 서비스의 내용·기능을 변경할 수 있으며, 중요한 변경 사항은 사전에 공지합니다.
            </p>
          </div>
        </Section>

        {/* 제7조 */}
        <Section number="제7조" title="회원의 의무">
          <div className="text-sm text-gray-700 leading-relaxed space-y-2">
            <p>
              ① 회원은 자신의 계정 정보(이메일, 비밀번호)를 안전하게 관리해야 하며, 계정을 제3자와
              공유하거나 양도할 수 없습니다.
            </p>
            <p>② 회원은 서비스를 이용하여 다음 행위를 해서는 안 됩니다.</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>타인의 개인정보 또는 계정 정보를 무단으로 수집·사용하는 행위</li>
              <li>
                서비스의 안정적 운영을 방해하는 행위 (과도한 트래픽 발생, 해킹 시도, 크롤링 등)
              </li>
              <li>다른 회원의 서비스 이용을 방해하거나 개인정보를 침해하는 행위</li>
              <li>운영자의 사전 허가 없이 서비스를 이용한 영리 목적 활동</li>
              <li>관련 법령에 위반되는 행위</li>
            </ul>
          </div>
        </Section>

        {/* 제8조 */}
        <Section number="제8조" title="지식재산권">
          <div className="text-sm text-gray-700 leading-relaxed space-y-2">
            <p>
              ① 서비스 내 운영자가 제공하는 콘텐츠, 디자인, 코드 등의 지식재산권은 운영자에게 귀속됩니다.
            </p>
            <p>
              ② 회원이 서비스 내에 등록한 데이터(요구사항, 태스크, 문서 등)의 지식재산권은 해당 회원 또는
              조직에 귀속됩니다.
            </p>
          </div>
        </Section>

        {/* 제9조 */}
        <Section number="제9조" title="책임 제한">
          <div className="text-sm text-gray-700 leading-relaxed space-y-2">
            <p>
              ① 운영자는 천재지변, 전쟁, 인터넷 장애, 서버 업체의 귀책 사유 등 불가항력적 사유로 인한
              서비스 중단에 대해 책임을 지지 않습니다.
            </p>
            <p>
              ② 운영자는 회원이 서비스를 이용하여 얻은 결과물에 대한 책임을 지지 않습니다.
            </p>
            <p>
              ③ 운영자는 회원 간 또는 회원과 제3자 간 서비스를 매개로 발생한 분쟁에 개입하지 않으며,
              이로 인한 손해를 배상할 책임이 없습니다.
            </p>
            <p>
              ④ 운영자는 무료로 제공되는 서비스와 관련하여 관련 법령에 특별한 규정이 없는 한 손해를
              배상하지 않습니다.
            </p>
          </div>
        </Section>

        {/* 제10조 */}
        <Section number="제10조" title="서비스 이용 제한 및 계약 해지">
          <div className="text-sm text-gray-700 leading-relaxed space-y-2">
            <p>
              ① 운영자는 회원이 제7조를 위반한 경우 서비스 이용을 제한하거나 이용계약을 해지할 수
              있습니다.
            </p>
            <p>
              ② 회원은 언제든지 서비스 내 [계정 설정 → 계정 삭제] 메뉴를 통해 탈퇴할 수 있습니다.
            </p>
          </div>
        </Section>

        {/* 제11조 */}
        <Section number="제11조" title="분쟁 해결">
          <div className="text-sm text-gray-700 leading-relaxed space-y-2">
            <p>
              ① 서비스 이용과 관련하여 발생한 분쟁은 운영자와 회원이 상호 협의하여 해결합니다.
            </p>
            <p>
              ② 협의가 이루어지지 않는 경우, 민사소송법에 따른 관할 법원을 제1심 법원으로 합니다.
            </p>
          </div>
        </Section>

        {/* 제12조 */}
        <Section number="제12조" title="준거법">
          <p className="text-sm text-gray-700 leading-relaxed">
            이 약관은 대한민국 법률을 준거법으로 합니다.
          </p>
        </Section>

        {/* 부칙 */}
        <section className="mt-10">
          <h2 className="text-base font-bold text-gray-900 mb-3">부칙</h2>
          <p className="text-sm text-gray-700 leading-relaxed">
            이 약관은 <strong>2026년 7월 3일</strong>부터 시행합니다.
          </p>
        </section>

        <div className="mt-12 pt-6 border-t border-gray-200 text-xs text-gray-400 text-center">
          © 2026 MaioS. All rights reserved.
        </div>
      </main>
    </div>
  );
}
